import React, { useContext, useEffect, useState } from "react"
import * as z from "zod"
import Loader from "../components/ui/loader"
import secureLocalStorage from "react-secure-storage"
import { Button } from "../components/ui/button"
import { CommonFunctionCtx } from "../context/commonFunctionContext"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog"
import { EmailLogin } from "../api/auth.service"
import { FieldError } from "../components/ui/field-error"
import { getUserStats } from "../utils/getUserStats"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../components/ui/input-otp"
import { SvgZoeeGradientFavicon } from "../components/icons/logos/zoee-gradient-favicon"
import { useNavigate } from "react-router-dom"
import { UserCtx } from "../context/userContext"
import { UserInfo } from "../api/app.service"
import { useToast } from "../hooks/useToast"
import { VerifyConfirmationCode } from "../api/auth.service"

interface Props {
  guestCheckout?: boolean
  onVerified?: () => void
  isTemp?: number
}

const ForceVerifyEmail: React.FC<Props> = ({
  guestCheckout,
  onVerified,
  isTemp,
}) => {
  const { user } = useContext(UserCtx)
  const { getUser } = useContext(CommonFunctionCtx)
  const { toast } = useToast()
  const [disableResend, setDisableResend] = useState(false)
  const [sessionData, setSessionData] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [oneTimePassword, setOneTimePassword] = useState("")
  const [isOpen, setIsOpen] = useState(true)
  const otpSent = secureLocalStorage.getItem("Verification Sent")

  const navigate = useNavigate()
  const coachRedirect = localStorage.getItem("coachRedirect")
  const memberRedirect = localStorage.getItem("memberRedirect")
  const redirectPath =
    (user?.activeProfile === "coach" && coachRedirect) ||
    (user?.activeProfile === "member" && memberRedirect) ||
    localStorage.getItem("currentLocation") ||
    "/"

  const sendVerifyEmail = async () => {
    setDisableResend(true)
    setLoading(true)

    try {
      if (user?.email) {
        const data = await EmailLogin.loginOTP({ email: user.email })
        setSessionData(data)
        secureLocalStorage.setItem("Verification Sent", "true")
      }
    } catch (ex) {
      console.error(ex)
      toast.error("Failed to resend OTP")
    } finally {
      setLoading(false)
      setTimeout(() => setDisableResend(false), 15000)
    }
  }

  useEffect(() => {
    if (
      isTemp !== 2 &&
      localStorage.getItem("currentLocation") === "/sign-in"
    ) {
      localStorage.setItem("currentLocation", `/${user.activeProfile}`)
    }
  }, [])

  const safeRedirectPath =
    redirectPath ||
    localStorage.getItem("currentLocation") ||
    `/${user.activeProfile}`

  const checkForVerification = async () => {
    toast.clearAll()
    await getUser()

    const userToValidateEmail = await UserInfo.getUser({
      user_id: parseInt(user.user_id),
    })

    if (!userToValidateEmail.user_data.email_verified) {
      toast.error("Please check your email to verify")
    } else if (isTemp !== 2) {
      localStorage.removeItem("hasSentVerification")
      navigate(safeRedirectPath)
    } else {
      setIsOpen(false)
      onVerified?.()
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setError(undefined)

    const parsed = z
      .string()
      .length(6, { message: "Must be exactly 6 digits long" })
      .regex(/^\d+$/, { message: "Must contain only digits" })
      .safeParse(oneTimePassword)

    if (!parsed.success) {
      setError(parsed.error.issues[0].message)
      setLoading(false)
      return
    }

    try {
      await VerifyConfirmationCode.verifyConfirmationCode({
        confirmation_code: parsed.data,
        session: sessionData?.session,
        user_id: sessionData?.user_id,
        stats: getUserStats(),
      })

      await checkForVerification()
    } catch (ex) {
      console.error(ex)
      setError("Invalid or expired code")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!otpSent) {
      sendVerifyEmail()
    }
  }, [])

  return (
    <Dialog open={isOpen} modal={true}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-center">
            We've sent you a one time password!
          </DialogTitle>
        </DialogHeader>

        <DialogDescription className="text-center">
          Please check your inbox for a message from no-reply@zoee.com. Input
          the code you've received and unlock a world of possibilities! Let us
          know how we can best support you on your journey.
        </DialogDescription>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4 bg-white p-6 rounded-xl items-center"
        >
          <div className="flex flex-col items-center">
            <InputOTP
              id="otp"
              maxLength={6}
              value={oneTimePassword}
              onChange={(value) => setOneTimePassword(value)}
              disabled={loading}
              autoFocus
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} className="bg-white" />
                <InputOTPSlot index={1} className="bg-white" />
                <InputOTPSlot index={2} className="bg-white" />
                <InputOTPSlot index={3} className="bg-white" />
                <InputOTPSlot index={4} className="bg-white" />
                <InputOTPSlot index={5} className="bg-white" />
              </InputOTPGroup>
            </InputOTP>

            {/* error shows directly under the slots */}
            <FieldError error={error} />
          </div>
          <div className="flex flex-col gap-2">
            <Button
              variant="link"
              type="button"
              onClick={() => sendVerifyEmail()}
              disabled={disableResend}
            >
              Resend One-time Password
            </Button>
          </div>
          <Button className="w-full" disabled={loading}>
            {loading ? <Loader /> : "Verify"}
          </Button>
        </form>

        <DialogDescription className="text-center">
          We take the privacy and security of your account very seriously.
        </DialogDescription>

        <SvgZoeeGradientFavicon className="mx-auto mb-[-35px] w-12 h-12" />
      </DialogContent>
    </Dialog>
  )
}

export default ForceVerifyEmail
